// ============================================================================
// ui/replay.ts — DETERMINISTIC MATCH REWATCH (the post-game "god mode" playback).
//
// The sim is bit-deterministic: every random draw comes from a mulberry32 stream seeded off
// state.seed, and the bot is a pure function of that stream + the sim state (it re-runs inside
// update()). So a full match is reproducible from just { seed, difficulty } + the human command
// log. We DON'T record the bot — re-simulating regenerates its every move identically, as long as
// we inject the human commands at the exact same ticks (any drift shifts the shared rng stream).
//
// This module rebuilds a fresh sim from the recording and plays it forward under god-view (no fog,
// whole map) with play/pause, speed, restart, and a scrubbable timeline. Backward seeks rebuild the
// sim from tick 0 and fast-forward (cheap — the headless sim runs thousands of ticks in a blink).
// It mounts its OWN render on the given canvas, so the caller must first tear down the live match
// view (stop the loop, destroy the live render + inputs) to avoid double-drawing.
// ============================================================================
import { createSim } from '../sim/sim.js';
import { mountRender } from '../render/render.js';
import { CAMERA, WORLD } from '../config.js';
import type { Command, Difficulty } from '../types.js';

/** One recorded human command and the tick (completed-update count) it was issued at. */
export interface RecordedCmd {
  t: number; // tick index = number of sim.update() calls that had advanced when it was issued
  c: Command;
}
export interface ReplayRecording {
  seed: number;
  difficulty: Difficulty;
  endTick: number; // total ticks the match ran (for the timeline length)
  cmds: RecordedCmd[];
}

const STEP = 1 / 30; // fixed sim timestep — must match the live loop (createLoop step 1/30)
const SPEEDS = [1, 4, 16];

export function startReplay(
  canvas: HTMLCanvasElement,
  rec: ReplayRecording,
  onExit: () => void,
): void {
  // Bucket commands by tick once, preserving submission order within a tick.
  const byTick = new Map<number, Command[]>();
  for (const { t, c } of rec.cmds) {
    const arr = byTick.get(t);
    if (arr) arr.push(c);
    else byTick.set(t, [c]);
  }
  const total = Math.max(1, rec.endTick);

  // God-view perk: pull the zoom floor back ~3× (like the sandbox) so pinch-out can frame the WHOLE
  // battlefield, which the in-game floor (0.233) can't on a tall portrait map. Temporary + restored
  // on exit — safe because the live match view is already torn down while the replay owns the screen.
  const savedZoomMin = CAMERA.zoomMin;
  CAMERA.zoomMin = savedZoomMin / 3;

  // ---- sim + render, rebuildable for restart / backward seek ----
  let sim = createSim({ seed: rec.seed, difficulty: rec.difficulty });
  let view = mountRender(canvas, sim, { godView: true });
  let tick = 0;

  function teardownRender(): void {
    view.destroy();
  }
  function rebuild(): void {
    teardownRender();
    sim = createSim({ seed: rec.seed, difficulty: rec.difficulty });
    view = mountRender(canvas, sim, { godView: true });
    tick = 0;
  }

  // Advance exactly one tick: inject this tick's commands (in order), then step the sim.
  function stepOnce(): void {
    const cs = byTick.get(tick);
    if (cs) for (const c of cs) sim.command(c);
    sim.update(STEP);
    sim.state.events.length = 0; // drain the transient event queue (the UI normally owns this)
    tick++;
  }
  // Fast-forward to an absolute tick with no rendering (used by the scrubber).
  function seekTo(target: number): void {
    const tgt = Math.max(0, Math.min(total, Math.floor(target)));
    if (tgt < tick) rebuild(); // can't step backwards — re-sim from 0
    let guard = 0;
    while (tick < tgt && !sim.state.result.over && guard < 500_000) {
      stepOnce();
      guard++;
    }
  }

  // ---- free camera: watch-only pan + zoom (no unit commands), mirrors the sandbox god-view ----
  // The renderer drives view.cam.update() each frame; we only push pan/zoom intent. touch-action:none
  // stops the browser stealing the gesture. Handlers read `view.cam` live so a rebuild (restart/seek)
  // swaps to the fresh camera seamlessly.
  const prevTouchAction = canvas.style.touchAction;
  canvas.style.touchAction = 'none';
  const cptrs = new Map<number, { x: number; y: number }>();
  let pinchDist = 0;
  let lastTap = 0;
  const lastTapAt = { x: 0, y: 0 };
  const localXY = (e: PointerEvent): { x: number; y: number } => {
    const r = canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const cCentroid = (): { x: number; y: number } => {
    let x = 0, y = 0;
    for (const p of cptrs.values()) { x += p.x; y += p.y; }
    const n = cptrs.size || 1;
    return { x: x / n, y: y / n };
  };
  const cSpread = (): number => {
    const pts = [...cptrs.values()];
    return pts.length < 2 ? 0 : Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
  };
  const refitWholeMap = (): void => {
    view.cam.jumpTo({ x: WORLD.width / 2, y: WORLD.height / 2 });
    view.cam.zoom = 0; // clamped to CAMERA.zoomMin -> whole-map overview
  };
  const onCamDown = (e: PointerEvent): void => {
    canvas.setPointerCapture(e.pointerId);
    cptrs.set(e.pointerId, localXY(e));
    if (cptrs.size === 2) pinchDist = cSpread();
  };
  const onCamMove = (e: PointerEvent): void => {
    const prev = cptrs.get(e.pointerId);
    if (!prev) return;
    const cur = localXY(e);
    if (cptrs.size === 1) {
      view.cam.pan(cur.x - prev.x, cur.y - prev.y);
      cptrs.set(e.pointerId, cur);
    } else if (cptrs.size === 2) {
      const beforeC = cCentroid();
      cptrs.set(e.pointerId, cur);
      const afterC = cCentroid();
      view.cam.pan(afterC.x - beforeC.x, afterC.y - beforeC.y); // two-finger drag
      const d = cSpread();
      if (pinchDist > 0 && d > 0) view.cam.pinchZoom(d / pinchDist, afterC); // pinch to zoom
      pinchDist = d;
    }
  };
  const onCamUp = (e: PointerEvent): void => {
    const t = performance.now();
    const p = localXY(e);
    if (t - lastTap < 300 && Math.hypot(p.x - lastTapAt.x, p.y - lastTapAt.y) < 24) {
      refitWholeMap(); // double-tap re-frames the whole battlefield
      lastTap = 0;
    } else {
      lastTap = t;
      lastTapAt.x = p.x;
      lastTapAt.y = p.y;
    }
    cptrs.delete(e.pointerId);
    if (cptrs.size < 2) pinchDist = 0;
    try { canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const onCamCancel = (e: PointerEvent): void => {
    cptrs.delete(e.pointerId);
    if (cptrs.size < 2) pinchDist = 0;
  };
  const onCamWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const r = canvas.getBoundingClientRect();
    view.cam.zoomAt(e.clientX - r.left, e.clientY - r.top, e.deltaY < 0 ? 1.1 : 1 / 1.1);
  };
  canvas.addEventListener('pointerdown', onCamDown);
  canvas.addEventListener('pointermove', onCamMove);
  canvas.addEventListener('pointerup', onCamUp);
  canvas.addEventListener('pointercancel', onCamCancel);
  canvas.addEventListener('wheel', onCamWheel, { passive: false });

  // ---- controls UI ----
  const bar = document.createElement('div');
  bar.className = 'replay-bar';
  const btn = (label: string, cls = ''): HTMLButtonElement => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `replay-btn ${cls}`.trim();
    b.textContent = label;
    return b;
  };
  const exitBtn = btn('✕', 'replay-exit');
  const restartBtn = btn('⟲');
  const playBtn = btn('⏸', 'replay-play');
  const speedBtn = btn('1×', 'replay-speed');
  const clock = document.createElement('span');
  clock.className = 'replay-clock';

  const track = document.createElement('div');
  track.className = 'replay-track';
  const fill = document.createElement('div');
  fill.className = 'replay-fill';
  track.appendChild(fill);

  const row = document.createElement('div');
  row.className = 'replay-row';
  row.append(exitBtn, restartBtn, playBtn, speedBtn, clock);
  bar.append(track, row);
  document.body.appendChild(bar);

  // ---- playback state ----
  let playing = true;
  let speedIdx = 0;
  let acc = 0;
  let last = 0;
  let raf = 0;

  const mmss = (ticks: number): string => {
    const s = Math.floor(ticks / 30);
    return `${String((s / 60) | 0).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  };
  function paintControls(): void {
    const over = sim.state.result.over;
    playBtn.textContent = over ? '↺' : playing ? '⏸' : '▶';
    speedBtn.textContent = `${SPEEDS[speedIdx]}×`;
    fill.style.width = `${Math.min(100, (tick / total) * 100)}%`;
    clock.textContent = `${mmss(tick)} / ${mmss(total)}`;
  }

  function frame(now: number): void {
    raf = requestAnimationFrame(frame);
    if (!last) last = now;
    let dt = (now - last) / 1000;
    last = now;
    if (dt > 0.25) dt = 0.25; // clamp a backgrounded tab so it doesn't fast-forward on return
    if (playing && !sim.state.result.over) {
      acc += dt * SPEEDS[speedIdx];
      let guard = 0;
      while (acc >= STEP && !sim.state.result.over && guard < 4000) {
        stepOnce();
        acc -= STEP;
        guard++;
      }
      if (sim.state.result.over) playing = false; // auto-stop at the end
    }
    view.render(1);
    paintControls();
  }

  playBtn.addEventListener('click', () => {
    if (sim.state.result.over) {
      rebuild(); // "↺" replay-from-start once the match has ended
      acc = 0;
      playing = true;
    } else {
      playing = !playing;
    }
    paintControls();
  });
  restartBtn.addEventListener('click', () => {
    rebuild();
    acc = 0;
    playing = true;
    paintControls();
  });
  speedBtn.addEventListener('click', () => {
    speedIdx = (speedIdx + 1) % SPEEDS.length;
    paintControls();
  });

  // Scrub: tap/drag anywhere on the timeline to seek.
  const scrub = (clientX: number): void => {
    const r = track.getBoundingClientRect();
    const f = r.width > 0 ? (clientX - r.left) / r.width : 0;
    seekTo(f * total);
    acc = 0;
    view.render(1);
    paintControls();
  };
  let scrubbing = false;
  track.addEventListener('pointerdown', (e) => {
    scrubbing = true;
    track.setPointerCapture(e.pointerId);
    scrub(e.clientX);
  });
  track.addEventListener('pointermove', (e) => {
    if (scrubbing) scrub(e.clientX);
  });
  const endScrub = (): void => {
    scrubbing = false;
  };
  track.addEventListener('pointerup', endScrub);
  track.addEventListener('pointercancel', endScrub);

  exitBtn.addEventListener('click', () => {
    cancelAnimationFrame(raf);
    teardownRender();
    bar.remove();
    // Detach the free-camera handlers + restore the shared zoom clamp / canvas gesture handling.
    canvas.removeEventListener('pointerdown', onCamDown);
    canvas.removeEventListener('pointermove', onCamMove);
    canvas.removeEventListener('pointerup', onCamUp);
    canvas.removeEventListener('pointercancel', onCamCancel);
    canvas.removeEventListener('wheel', onCamWheel);
    canvas.style.touchAction = prevTouchAction;
    CAMERA.zoomMin = savedZoomMin;
    onExit();
  });

  paintControls();
  raf = requestAnimationFrame(frame);
}
